import * as React from 'react';

import { CURRENCIES } from 'src/features/payStations/types';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';

type ParkingContextType = {
    parkingId: string;
    parkingName: string;
    parkingRole: MEMBERSHIP_ROLES;
    parkingCurrency: CURRENCIES;
    setParkingCurrency: (parkingCurrency: CURRENCIES) => void;
    setParkingId: (parkingId: string) => void;
    setParkingName: (parkingName: string) => void;
    setParkingRole: (parkingRole: MEMBERSHIP_ROLES) => void;
};

const ParkingContext = React.createContext<ParkingContextType | null>(null);

export const useParking = () => {
    const context = React.useContext(ParkingContext);

    if (!context) {
        throw new Error('useParking must be used within a ParkingProvider');
    }
    return context;
};

export const ParkingContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [parkingId, setParkingId] = React.useState<ParkingContextType['parkingId']>(() => {
        return localStorage.getItem('parkingId') || '';
    });

    const [parkingName, setParkingName] = React.useState<ParkingContextType['parkingName']>(() => {
        return localStorage.getItem('parkingName') || '';
    });

    const [parkingRole, setParkingRole] = React.useState<ParkingContextType['parkingRole']>(() => {
        return (localStorage.getItem('parkingRole') || '') as MEMBERSHIP_ROLES;
    });

    const [parkingCurrency, setParkingCurrency] = React.useState<ParkingContextType['parkingCurrency']>(() => {
        return (localStorage.getItem('parkingCurrency') || CURRENCIES.EUR) as CURRENCIES;
    });

    const value = React.useMemo(() => {
        return {
            parkingId,
            parkingName,
            parkingRole,
            parkingCurrency,
            setParkingCurrency,
            setParkingId,
            setParkingName,
            setParkingRole,
        };
    }, [parkingId, parkingName, parkingRole, parkingCurrency]);

    React.useEffect(() => {
        localStorage.setItem('parkingId', parkingId);
    }, [parkingId]);

    React.useEffect(() => {
        localStorage.setItem('parkingName', parkingName);
    }, [parkingName]);

    React.useEffect(() => {
        localStorage.setItem('parkingRole', parkingRole);
    }, [parkingRole]);

    React.useEffect(() => {
        localStorage.setItem('parkingCurrency', parkingCurrency);
    }, [parkingCurrency]);

    return <ParkingContext.Provider value={value}>{children} </ParkingContext.Provider>;
};
